<template>
  <v-dialog
    v-model="dialog"
    width="50%"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-card>
      <v-card-title class="headline">{{ textTitle }}</v-card-title>
      <v-card-text>
        <v-card :loading="loading">
          <v-card-title>
            <v-spacer></v-spacer>
            <v-btn color="info" @click="onAddAttachment()">
              <v-icon>mdi-plus</v-icon> Add Files</v-btn
            >
          </v-card-title>
          <v-skeleton-loader
            v-if="isLoading"
            class="mx-auto"
            type="table"
          ></v-skeleton-loader>
          <v-data-table
            v-else
            :headers="activeHeaders"
            :items="allItems"
            class="elevation-1"
            :options.sync="tableOptions"
            :server-items-length="totalItems"
            :hide-default-footer="true"
            :footer-props="{
              'items-per-page-options': [10, 20, 30, 40, 50, 100, 200],
            }"
          >
            <template v-slot:top>
              <v-toolbar flat>
                <v-menu offset-y :close-on-content-click="false">
                  <template v-slot:activator="{ on }">
                    <v-btn rounded class="content-ma-0 mr-2" v-on="on" x-small>
                      <v-icon small>menu</v-icon> Customize Columns
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item dense :key="i" v-for="(header, i) in headers">
                      <v-list-item-title v-if="header.readonly">
                        <v-checkbox
                          v-model="headers[i].active"
                          :label="header.text"
                          :disabled="true"
                        />
                      </v-list-item-title>
                      <v-list-item-title v-else>
                        <v-checkbox
                          v-model="headers[i].active"
                          :label="header.text"
                        />
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-toolbar>
            </template>

            <template v-slot:item.lastUpdated="{ item }">
              <v-chip @click="onEdit(item)" small>
                {{ item.lastUpdated | formatDatetime }}
              </v-chip>
            </template>

            <template v-slot:item.dateCreated="{ item }">
              <v-chip @click="onEdit(item)" small>
                {{ item.dateCreated | formatDatetime }}
              </v-chip>
            </template>

            <template v-slot:item.categories="{ item }">
              <TagsChip :value="item.categories" />
            </template>

            <template v-slot:item.date="{ item }">
              <v-chip small @click="onEdit(item)">
                {{ item.date | formatDate }}
              </v-chip>
            </template>

            <template v-slot:item.userId="{ item }">
              <span @click="onEdit(item)">
                <UsersChip :value="item.taskUsers" />
              </span>
            </template>

            <template v-slot:item.size="{ item }">
              <div>
                <span v-if="!item.isFolder">
                  {{ item.size ? bytesToSize(item.size) : "" }}
                </span>
                <small v-else> {{ item.totalFiles }} File </small>
              </div>
            </template>

            <template v-slot:item.status="{ item }">
              <span @click="onEdit(item)"
                ><TaskStatusChip :status="item.status"
              /></span>
            </template>

            <template v-slot:item.name="{ item }">
              <span
                @click="openFolder(item)"
                v-if="item.isFolder"
                style="cursor: pointer"
              >
                <v-icon>{{ item.icon }}</v-icon> {{ item.name }}
              </span>

              <span v-else>
                <v-icon>{{ item.icon }}</v-icon> {{ item.name }}
              </span>
            </template>

            <template v-slot:item.type="{ item }" @click="onEdit(item)">
              <span>
                {{ item.type }}
              </span>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-menu offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn icon small class="content-ma-0" v-on="on">
                    <v-icon small>mdi-cogs</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    @click="onDownload(item)"
                    v-show="hasAccess('task.edit') && !item.isFolder"
                  >
                    <v-list-item-title>
                      <v-icon small left>mdi-download</v-icon>Download
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    @click="onDelete(item)"
                    v-show="hasAccess('task.delete')"
                  >
                    <v-list-item-title>
                      <v-icon small left>delete</v-icon>Detach
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>

      <v-card-actions>
        <v-btn @click="closeDelete">Close</v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
    <DeleteAttachedFile
      :show="showDelete"
      :item="mixedItem"
      :object="object"
      @onClosed="clearDelete()"
    />
    <FilesExplorerDialog
      :show="showModal"
      :item="item"
      :excludedItems="allItems"
      :object="object"
      @onClosed="clearAddAttachment()"
    />
  </v-dialog>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import formatterMixin from "@/mixins/formatterMixin";
import TaskStatusChip from "src/components/TaskStatusChip";
import UsersChip from "components/UsersChip";
import DeleteAttachedFile from "./_DeleteAttachedFile.vue";
import _ from "lodash";
import { getStorage, setStorage } from "src/utils/helpers";
import TagsChip from "components/TagsChip";
import FilesExplorerDialog from "components/FilesAttachment/FilesExplorerDialog";

const SETTING_LIST_KEY = "file-attachments-columns";
const defaultHeaders = [
  {
    text: "Action",
    value: "actions",
    sortable: false,
    readonly: true,
    active: true,
    width: "5%",
  },
  { text: "Name", value: "name", sortable: true, active: true },
  { text: "Size", value: "size", sortable: true, active: true },
  { text: "Type", value: "type", sortable: true, active: true },
  { text: "Notes", value: "notes", active: true },
  { text: "Tags", value: "categories", active: true },
  {
    text: "Date Created",
    value: "dateCreated",
    sortable: true,
    active: false,
  },
  {
    text: "Date Modified",
    value: "lastUpdated",
    sortable: true,
    active: false,
  },
];
export default {
  mixins: [formatterMixin],

  props: {
    show: Boolean,
    item: Object,
    clientId: Number,
    hideTitle: Boolean,
    object: String,
  },

  components: {
    TaskStatusChip,
    UsersChip,
    TagsChip,
    FilesExplorerDialog,
    DeleteAttachedFile,
  },
  data: () => ({
    dialog: false,
    isEdit: false,
    valid: false,
    namespace: "Storage",
    page: "Files",

    tableOptions: {},

    filter: {
      SearchTerm: "",
      clientId: "",
      tags: [],
    },

    menu: false,
    headers: defaultHeaders.map((item) => {
      let savedHeaders = getStorage(SETTING_LIST_KEY) || [];
      let nowHeader = savedHeaders.filter(
        (saved) => saved.value === item.value
      );

      if (nowHeader) {
        nowHeader = nowHeader[0];
      }
      return {
        ...item,
        ...nowHeader,
      };
    }),
    tasks: [],
    showModal: false,
    showForm: false,
    showFormFolder: false,
    showDelete: false,
    showDeleteFolder: false,
    showTimeClock: false,
    showTimeEntryForm: false,
    selectedItem: {},
  }),
  computed: {
    ...mapState({
      currentItem: (state) => state.item,
      alert: (state) => state.alert,
    }),
    ...mapGetters("account", ["hasAccess"]),
    ...mapState({
      account: (state) => state.account.data,
      totalItems: (state) => state.storage.metaData.totalEntries,
    }),

    loading() {
      return this.$store.state[this.object].loadingTable;
    },

    allItems() {
      return this.$store.state[this.object].singleAttachments;
    },

    textTitle() {
      if (this.item && this.item.id) {
        return "Associated Files of #" + this.item.code;
      } else {
        return "Associated File";
      }
    },

    mixedItem() {
      return {
        item: this.item,
        file: this.selectedItem,
      };
    },

    folderPath() {
      let selected = this.mapFolders.filter((item) => {
        return item.id == this.folderId;
      });
      if (selected.length == 1) {
        //  { ...selected[0], pathName: `Home / ${selected[0].pathName}` };

        let pathIds = selected[0].pathIds;
        let pathNames = selected[0].pathName.split("/");

        let ww = pathIds.map((id, idx) => {
          return [id, pathNames[idx]];
        });
        return [["", "Home"], ...ww];
      } else {
        return [["", "Home"]];
      }
    },

    mapFolders() {
      return this.$store.getters["storageFolder/getAll"];
    },

    folderId() {
      let { folderId } = this.$route.query;
      return folderId;
    },

    isLoading() {
      return this.loading && this.allItems.length == 0;
    },

    computedFilter() {
      let { clientId, SearchTerm, tags } = this.filter;
      let { sortBy, page, sortDesc, itemsPerPage } = this.tableOptions;
      let params = {
        PageNumber: page ? page : 1,
        PageSize: itemsPerPage > 0 ? itemsPerPage : 1000,
      };

      params.OrderBy = sortBy.length > 0 && sortBy[0] ? sortBy[0] : null;

      if (params.OrderBy) {
        params.Ascending = sortDesc.length > 0 && sortDesc[0] ? false : true;
      } else {
        params.Ascending = false;
      }

      if (clientId) {
        params.clientId = clientId;
      }

      if (SearchTerm) {
        params.SearchTerm = SearchTerm;
      }

      if (this.folderId) {
        params.folderId = this.folderId;
      }

      if (tags && tags.length > 0) {
        params.tags = tags;
      }
      return params;
    },

    activeHeaders() {
      return this.headers.filter((header) => header.active);
    },

    getClientId() {
      const { clientId } = this.$route.params;
      if (clientId) {
        return parseInt(clientId);
      }
      if (this.clientId) {
        return this.clientId;
      }
      return false;
    },

    tags() {
      return this.filter.tags;
    },
  },
  watch: {
    show(n, o) {
      if (n && !o) {
        this.dialog = true;
      }
    },

    dialog(n) {
      if (!n) {
        this.closeDelete();
      }
    },

    async item(newItem) {
      if (newItem && newItem.id) {
        await this.getAttachments(newItem.id);
      }
    },

    async folderId() {
      await this.refresh();
    },
    tags() {
      this.taskSearch(this.filter);
    },
    headers: {
      handler() {
        setStorage(SETTING_LIST_KEY, this.headers);
      },
      deep: true,
    },
    tableOptions: {
      handler(n, o) {
        if (JSON.stringify(n) !== JSON.stringify(o)) {
          this.refresh();
        }
      },
      deep: true,
    },

    filter: {
      async handler() {
        await this.refresh(true);
      },
      deep: true,
    },
  },
  async mounted() {
    if (this.clientId) {
      this.filter.clientId = this.clientId;
    }

    await this.loadFolders();
  },

  beforeDestroy() {
    clearInterval(this.timer);
  },

  methods: {
    capitalize(s) {
      return s[0].toUpperCase() + s.slice(1);
    },

    bytesToSize(bytes) {
      var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      if (bytes == 0) return "0 Byte";
      var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
      return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
    },

    ...mapActions({
      taskDelete: "storage/delete",
      taskSearch: "storage/search",
      downloadFile: "storage/download",
      loadFolders: "storageFolder/getAll",
    }),

    getAttachments(id) {
      return this.$store.dispatch(`${this.object}/getAttachments`, id);
    },

    refresh: _.debounce(async function () {
      this.getAttachments(this.item.id);
    }, 500),

    openFolder(item) {
      this.$router.push(`?folderId=${item.id}`);
    },

    onAddAttachment() {
      this.showModal = true;
      this.selectedItem = {};
    },

    onAddFolder() {
      this.showFormFolder = true;
      this.selectedItem = {};
    },
    onEdit(item) {
      if (!this.hasAccess("task.edit")) {
        return false;
      }
      if (item.isFolder) {
        this.showFormFolder = true;
      } else {
        this.showForm = true;
      }
      this.selectedItem = item;
    },

    async clearAddAttachment() {
      this.showModal = false;
      this.selectedItem = {};
      await this.refresh();
    },

    clearAddFolder() {
      this.showFormFolder = false;
      this.selectedItem = {};
    },

    async clearDelete() {
      this.showDelete = false;
      this.showDeleteFolder = false;
      this.selectedItem = {};
      await this.refresh();
    },

    onDelete(item) {
      console.log(item);
      if (item.isFolder) {
        this.showDeleteFolder = true;
      } else {
        this.showDelete = true;
      }
      this.selectedItem = item;
    },

    async onDownload(item) {
      await this.downloadFile(item);
    },
    ...mapActions("item", ["delete"]),
    closeDelete() {
      this.dialog = false;
      this.$store.dispatch(`${this.object}/clearSingleAttachments`);
      this.$emit("onClosed");
    },
  },
};
</script>